import moment from 'moment';

import { DATE_FORMAT_OUTPUT, MESSAGES_BLOCK_COUNT } from '../ConversationActions';

// Export KakaoTalk officiel : un .txt par conversation (« 대화 내용 내보내기 » /
// « Export chat »). Pas de médias dans ce format : photos, vidéos et emoticônes
// apparaissent comme mots-clés localisés (사진, Photo, Emoticon, ...).
//
// Deux familles de fichiers :
// - classique : la date complète est sur chaque ligne
//   « 2024. 1. 15. 오전 9:00, Alice : Bonjour »
// - récent : séparateurs de jour puis lignes entre crochets
//   « --------------- 2024년 1월 15일 월요일 --------------- »
//   « [Alice] [오전 9:00] Bonjour »
//
// Locales supportées pour les en-têtes, méridiens et séparateurs de date :
// coréen et anglais (langues d'export dominantes) plus les 7 langues du site.

const MERIDIEM = '(오전|오후|AM|PM|A\\.M\\.|P\\.M\\.|午前|午後)';

// Heure 12h (préfixe coréen ou suffixe AM/PM) ou 24h. 4 groupes capturants.
const TIME_SRC = `(?:${MERIDIEM}\\s*)?([0-2]?\\d)\\s*[:.]\\s*([0-5]\\d)(?:\\s*${MERIDIEM})?`;

// Date numérique : 2024. 1. 15. / 2024년 1월 15일 / 2024-01-15. 3 groupes.
const NUMERIC_DATE_SRC = '(\\d{4})\\s*[.\\-/년年]\\s*(\\d{1,2})\\s*[.\\-/월月]\\s*(\\d{1,2})\\s*[.일日]?';

const WEEKDAY_SRC = '(?:[\\s,]+[A-Za-zÀ-ÿ가-힣.]+)?';

const CLASSIC_MESSAGE_REGEX = new RegExp(
    `^${NUMERIC_DATE_SRC}\\s+${TIME_SRC}\\s*,\\s*(.+?)\\s*:\\s+([\\s\\S]*)$`,
    'i',
);

// Même préfixe sans expéditeur : notification système (entrée / sortie de salon).
const CLASSIC_PREFIX_REGEX = new RegExp(
    `^${NUMERIC_DATE_SRC}\\s+${TIME_SRC}\\s*,`,
    'i',
);

const RECENT_MESSAGE_REGEX = new RegExp(
    `^\\[(.+?)\\]\\s*\\[${TIME_SRC}\\]\\s*([\\s\\S]*)$`,
    'i',
);

const NUMERIC_DIVIDER_REGEX = new RegExp(`^${NUMERIC_DATE_SRC}${WEEKDAY_SRC}$`);

// « Monday, January 15, 2024 » (anglais).
const MONTH_FIRST_DIVIDER_REGEX = /^(?:[A-Za-zÀ-ÿ]+,\s*)?([A-Za-zÀ-ÿ]+)\s+(\d{1,2}),?\s+(\d{4})$/;

// « lundi 15 janvier 2024 », « 15 de enero de 2024 » (langues du site).
const DAY_FIRST_DIVIDER_REGEX = /^(?:[A-Za-zÀ-ÿ]+,?\s+)?(\d{1,2})(?:\.|er|º)?\s+(?:de\s+|d')?([A-Za-zÀ-ÿ]+)\s+(?:de\s+)?(\d{4})$/i;

const MONTH_NAMES = {
    en: ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'],
    fr: ['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'],
    es: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
    de: ['januar', 'februar', 'marz', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'dezember'],
    it: ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'],
    nl: ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'],
    pt: ['janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'],
};

const MONTH_INDEX = buildMonthIndex();

const TITLE_PATTERNS = [
    /^(.+?)님과의\s*카카오톡\s*대화/,
    /^(.+?)님과 카카오톡 대화/,
    /^KakaoTalk\s+Chats?\s+with\s+(.+)/i,
    /^Chat(?:s)?\s+with\s+(.+)/i,
    /^Talk\s+with\s+(.+)/i,
    /^Conversation avec\s+(.+)/i,
    /^Discussion avec\s+(.+)/i,
    /^Chat con\s+(.+)/i,
    /^Conversaci[oó]n con\s+(.+)/i,
    /^Chat mit\s+(.+)/i,
    /^Unterhaltung mit\s+(.+)/i,
    /^Chat met\s+(.+)/i,
    /^Gesprek met\s+(.+)/i,
    /^Conversa com\s+(.+)/i,
    /^(.+?)さんとのトーク/,
];

const META_LINE_REGEXES = [
    /^저장한\s*날짜\s*[:：]/,
    /^저장일\s*[:：]/,
    /^Date Saved\s*[:：]/i,
    /^Saved on\s*[:：]/i,
    /^Save[d]? Date\s*[:：]/i,
    /^Enregistr[eé]\s+le\s*[:：]/i,
    /^Guardado el\s*[:：]/i,
    /^Gespeichert am\s*[:：]/i,
    /^Salvato il\s*[:：]/i,
    /^Opgeslagen op\s*[:：]/i,
    /^Guardado em\s*[:：]/i,
    /^保存日時\s*[:：]/,
];

// Notifications de salon : pas d'expéditeur, aucune valeur dans le livre.
const SYSTEM_LINE_REGEXES = [
    /님이 (?:들어왔|나갔)습니다\.?$/,
    /님을 초대했습니다\.?$/,
    /님이 방장이 되었습니다\.?$/,
    /^.+ (?:joined|left) the (?:chat|chatroom|room)\.?$/i,
    /^.+ (?:has|have) (?:joined|left).*$/i,
    /^.+ invited .+\.?$/i,
    /^.+ a rejoint (?:la|le) (?:discussion|salon)\.?$/i,
    /^.+ a quitt[eé] (?:la|le) (?:discussion|salon)\.?$/i,
];

// Messages supprimés : exclus (pas de contenu imprimable).
const DELETED_REGEXES = [
    /^삭제된 메시지입니다\.?$/,
    /^this message was deleted\.?$/i,
    /^deleted message\.?$/i,
    /^ce message a [eé]t[eé] supprim[eé]\.?$/i,
    /^message supprim[eé]\.?$/i,
    /^este mensaje fue eliminado\.?$/i,
    /^mensaje eliminado\.?$/i,
    /^diese nachricht wurde gel[oö]scht\.?$/i,
    /^gel[oö]schte nachricht\.?$/i,
    /^questo messaggio [eè] stato eliminato\.?$/i,
    /^messaggio eliminato\.?$/i,
    /^dit bericht is verwijderd\.?$/i,
    /^verwijderd bericht\.?$/i,
    /^esta mensagem foi (?:apagada|eliminada)\.?$/i,
    /^mensagem apagada\.?$/i,
];

const DATE_FORMATS = [
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD HH:mm',
];

export function readFile(file, options, onProgress) {
    if(isTextFile(file)) {
        return new Promise(resolve => {
            const reader = new FileReader();
            reader.onload = () => {
                onProgress(100);
                resolve([
                    {
                        path: file.name,
                        filename: file.name,
                        originalContent: reader.result,
                        content: transformEntry(reader.result),
                    },
                ]);
            };
            reader.readAsText(file, 'UTF-8');
        });
    }
    return Promise.reject(new Error('KakaotalkImportRequiresTxt'));
}

export function getFiles() {
    return Promise.resolve([]);
}

export function canHandleImages() {
    return false;
}

export function getProfileName(entries) {
    const conversation = getConversation(entries[0]);
    if(conversation.profileName) {
        return conversation.profileName;
    }
    return (conversation.participants || [])[0] || '';
}

export function getThreads() {
    // Un export KakaoTalk = une seule conversation (comme WhatsApp et LINE).
    return [];
}

export function getArchiveContent(entries, from, thread, options = { withImages: false, withMedias: false }, onProgress = percentile => {}) {
    const participants = [];
    const messagesBlocks = [];

    onProgress(1);

    let messages = [];
    (entries || []).forEach(entry => {
        const conversation = getConversation(entry);
        (conversation.participants || []).forEach(name => {
            if(name && !participants.includes(name)) {
                participants.push(name);
            }
        });
        messages = messages.concat(conversation.messages || []);
    });

    const profileName = from || getProfileName(entries);

    messages = messages
        .map(message => formatMessage(message, profileName, participants))
        .filter(message => message && message.messageCsv)
        .sort((messageA, messageB) => messageA.timestamp - messageB.timestamp);

    // Le titulaire de l'export doit être en tête de liste (bulle « moi »).
    if(profileName && participants.includes(profileName)) {
        participants.splice(participants.indexOf(profileName), 1);
        participants.unshift(profileName);
    }

    const iteration = Math.floor(messages.length / MESSAGES_BLOCK_COUNT);
    const percentile = 100 / (iteration + 1);

    for(let i = 0; i <= iteration; i += 1) {
        const messagesBlock = messages.slice(i * MESSAGES_BLOCK_COUNT, (i * MESSAGES_BLOCK_COUNT) + MESSAGES_BLOCK_COUNT);
        onProgress(percentile);

        if(messagesBlock.length) {
            let csv = '';
            messagesBlock.forEach(message => {
                csv += `${message.messageCsv}\n`;
            });

            messagesBlocks.push({
                sort: i,
                filename: `messages_${i + 1}.csv`,
                content: csv,
                messages: messagesBlock,
                start: new Date(messagesBlock[0].timestamp),
                end: new Date(messagesBlock[messagesBlock.length - 1].timestamp),
            });
        }
    }

    return {
        participants,
        messagesBlocks,
        images: [],
        medias: [],
    };
}

function buildMonthIndex() {
    const index = {};
    Object.keys(MONTH_NAMES).forEach(locale => {
        MONTH_NAMES[locale].forEach((name, position) => {
            index[name] = position + 1;
            index[name.slice(0, 3)] = index[name.slice(0, 3)] || position + 1;
        });
    });
    return index;
}

function isTextFile(file) {
    const type = `${(file || {}).type || ''}`.toLowerCase();
    const name = `${(file || {}).name || ''}`.toLowerCase();
    return type.includes('text') || type.includes('plain') || name.endsWith('.txt');
}

function getConversation(entry) {
    if(!entry) {
        return { participants: [], messages: [] };
    }
    if(entry.originalContent && typeof entry.originalContent === 'string') {
        return JSON.parse(transformEntry(entry.originalContent));
    }
    if(typeof entry.content === 'string') {
        try {
            return JSON.parse(entry.content);
        } catch (err) {
            return JSON.parse(transformEntry(entry.content));
        }
    }
    return entry.content || { participants: [], messages: [] };
}

function transformEntry(rawContent) {
    const lines = `${rawContent || ''}`
        .replace(/^\uFEFF/, '')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .split('\n');

    const participants = [];
    const messages = [];
    let currentDate = null;
    let contactName = '';
    let pending = null;

    lines.forEach(rawLine => {
        const line = normalizeLine(rawLine);

        if(!line) {
            return;
        }

        const divider = parseDateDivider(line);
        if(divider) {
            pending = flushPending(pending, messages, participants);
            currentDate = divider;
            return;
        }

        const classic = parseClassicMessage(line);
        if(classic) {
            pending = flushPending(pending, messages, participants);
            currentDate = classic.dateOnly;
            pending = classic;
            return;
        }

        const recent = parseRecentMessage(line, currentDate);
        if(recent) {
            pending = flushPending(pending, messages, participants);
            pending = recent;
            return;
        }

        // Notification système : on ferme le message en cours sans l'y coller.
        if(isSystemLine(line)) {
            pending = flushPending(pending, messages, participants);
            return;
        }

        // Avant le premier message : bloc d'en-tête (titre, date d'export).
        if(!pending && !messages.length) {
            contactName = contactName || extractContactFromTitle(line);
            return;
        }

        if(pending) {
            pending.text = pending.text ? `${pending.text}\n${line}` : line;
        }
    });

    flushPending(pending, messages, participants);

    const profileName = resolveProfileName(participants, contactName);

    return JSON.stringify({
        participants,
        messages,
        profileName,
        contactName,
    });
}

function flushPending(pending, messages, participants) {
    if(!pending) {
        return null;
    }
    if(!pending.text || !pending.text.trim()) {
        return null;
    }
    if(isDeletedMessage(pending.text)) {
        return null;
    }
    if(!participants.includes(pending.sender)) {
        participants.push(pending.sender);
    }
    messages.push(pending);
    return null;
}

function normalizeLine(line) {
    return `${line || ''}`
        .replace(/[\u200E\u200F\uFEFF\u200B]/g, '')
        .replace(/[\u202F\u00A0]/g, ' ')
        .replace(/\u3000/g, ' ')
        .replace(/\s+$/g, '');
}

function isMetaLine(line) {
    return META_LINE_REGEXES.some(regex => regex.test(line));
}

function isSystemLine(line) {
    if(SYSTEM_LINE_REGEXES.some(regex => regex.test(line))) {
        return true;
    }
    // Format classique : horodatage complet mais aucun expéditeur avant le texte.
    return CLASSIC_PREFIX_REGEX.test(line) && !CLASSIC_MESSAGE_REGEX.test(line);
}

function extractContactFromTitle(line) {
    if(isMetaLine(line)) {
        return '';
    }
    for(let i = 0; i < TITLE_PATTERNS.length; i += 1) {
        const match = line.match(TITLE_PATTERNS[i]);
        if(match && match[1]) {
            return match[1].trim();
        }
    }
    return '';
}

function parseDateDivider(line) {
    const cleaned = line
        .replace(/^[-–—=\s]+/, '')
        .replace(/[-–—=\s]+$/, '')
        .trim();

    if(!cleaned) {
        return null;
    }

    const numeric = cleaned.match(NUMERIC_DIVIDER_REGEX);
    if(numeric) {
        return toDateOnly(numeric[1], numeric[2], numeric[3]);
    }

    const monthFirst = cleaned.match(MONTH_FIRST_DIVIDER_REGEX);
    if(monthFirst) {
        const month = getMonthNumber(monthFirst[1]);
        if(month) {
            return toDateOnly(monthFirst[3], month, monthFirst[2]);
        }
    }

    const dayFirst = cleaned.match(DAY_FIRST_DIVIDER_REGEX);
    if(dayFirst) {
        const month = getMonthNumber(dayFirst[2]);
        if(month) {
            return toDateOnly(dayFirst[3], month, dayFirst[1]);
        }
    }

    return null;
}

function getMonthNumber(name) {
    const normalized = `${name || ''}`
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\.$/, '');
    return MONTH_INDEX[normalized] || null;
}

function toDateOnly(year, month, day) {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function parseClassicMessage(line) {
    const match = line.match(CLASSIC_MESSAGE_REGEX);
    if(!match) {
        return null;
    }

    const dateOnly = toDateOnly(match[1], match[2], match[3]);
    const meridiem = match[4] || match[7];
    const sender = (match[8] || '').trim();
    const text = (match[9] || '').trim();

    return buildPending(dateOnly, meridiem, match[5], match[6], sender, text);
}

function parseRecentMessage(line, currentDate) {
    if(!currentDate) {
        return null;
    }

    const match = line.match(RECENT_MESSAGE_REGEX);
    if(!match) {
        return null;
    }

    const meridiem = match[2] || match[5];
    const sender = (match[1] || '').trim();
    const text = (match[6] || '').trim();

    return buildPending(currentDate, meridiem, match[3], match[4], sender, text);
}

function buildPending(dateOnly, meridiem, hour, minute, sender, text) {
    if(!sender || !dateOnly) {
        return null;
    }

    const hour24 = toHour24(hour, meridiem);
    const date = moment(`${dateOnly} ${String(hour24).padStart(2, '0')}:${minute}:00`, DATE_FORMATS, true);
    if(!date.isValid()) {
        return null;
    }

    return {
        date: date.format(DATE_FORMAT_OUTPUT).split('+').shift(),
        timestamp: date.valueOf(),
        dateOnly,
        sender,
        text: `${text || ''}`.trim(),
    };
}

function toHour24(hour, meridiem) {
    let value = parseInt(hour, 10);
    const marker = `${meridiem || ''}`.toLowerCase();

    const isPm = marker === '오후' || marker === '午後' || marker.startsWith('p');
    const isAm = marker === '오전' || marker === '午前' || marker.startsWith('a');

    if(isPm && value < 12) {
        value += 12;
    }
    if(isAm && value === 12) {
        value = 0;
    }
    return value;
}

function isDeletedMessage(text) {
    const trimmed = `${text || ''}`.trim();
    return DELETED_REGEXES.some(regex => regex.test(trimmed));
}

function resolveProfileName(participants, contactName) {
    if(contactName) {
        const others = participants.filter(name => name !== contactName);
        if(others.length) {
            return others[0];
        }
    }
    return participants[0] || '';
}

function formatMessage(message, profileName, participants) {
    const sender = `${message.sender || ''}`.replace(/;/g, ',');
    const isSender = sender === profileName;
    const recipients = participants.filter(name => name !== sender);
    const to = (isSender ? (recipients[0] || profileName) : profileName).replace(/;/g, ',');

    const data = {
        date: message.date,
        sender,
        to,
        content: encodeURIComponent(message.text || ''),
        isSender: isSender ? 'true' : 'false',
        medias: '',
    };

    return {
        messageParticipant: sender,
        messageCsv: Object.keys(data).map(key => (data[key] || '')).join(';'),
        date: message.date,
        timestamp: message.timestamp,
        text: message.text,
        sender,
    };
}
